"use client";

import { useEffect, useState } from "react";

interface CondensedHeader {
  /** Callback ref for the scroll viewport the listener attaches to. */
  setViewportEl: (element: HTMLDivElement | null) => void;
  isCondensed: boolean;
}

/**
 * Collapses a detail modal's header once its body is scrolled. The listener
 * lives on the scroll viewport (a callback ref so it (re)attaches whenever the
 * viewport mounts). rAF-throttled, and the boolean only flips at the threshold
 * (with hysteresis) so it never re-renders per frame or flickers at the edge.
 */
export const useCondensedHeader = (): CondensedHeader => {
  const [viewportEl, setViewportEl] = useState<HTMLDivElement | null>(null);
  const [isCondensed, setIsCondensed] = useState(false);

  useEffect(() => {
    if (!viewportEl) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const y = viewportEl.scrollTop;
        // Condensing changes no layout height, so this can't oscillate; the small
        // hysteresis just keeps the shadow from twitching around the threshold.
        setIsCondensed((prev) => (prev ? y > 8 : y > 48));
      });
    };
    viewportEl.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      viewportEl.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [viewportEl]);

  return { setViewportEl, isCondensed };
};
